/**
 * Machine page › Machine history.
 *
 * The demo's `machineTab('history')` (line 2200): one timeline, newest first,
 * merging four streams — work orders (type, PM code, component, description,
 * downtime, all-in cost, root cause), critical fault events, service cases and
 * email threads — capped at 80 events. Beside it, an "Ask about this machine"
 * box with four starter questions, and the availability line for the last 12
 * days.
 *
 * Selecting a component elsewhere on the page filters the **work orders**,
 * which is the demo's behaviour (`if (comp) ws = ws.filter(w => w.component ===
 * comp)`); faults, cases and emails carry no component and stay.
 *
 * ## Loading
 *
 * The timeline renders once `work-orders` lands. Faults (`telemetry`), cases
 * (`service`) and emails (`comms`) merge in as their domains arrive; until then
 * the count line says which streams are still loading rather than pretending
 * the machine has none.
 *
 * ## `downtime_h` is nullable and that matters
 *
 * `null` means downtime was never recorded, which is not zero. The downtime
 * clause is printed only for a recorded value.
 *
 * UI layer.
 *
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Sparkles } from 'lucide-react';
import { useDrawer } from '@/app/providers';
import type { SourceDocPayload } from '../../chrome-oem/SourceDrawerBody';
import { askOem } from '../../shared/assistant/ask';
import { MACHINE_ASK_STARTERS, machineAskPrompt } from '../../shared/seed/machine-ask';
import { unpackTable } from '../../shared/columnar';
import { workOrderAllInCalc } from '../../shared/source-calc';
import { useOemData, type ShiftDailyRow, type WorkOrder } from '../../shared/useOemData';
import { LineChart } from '../../shared/viz/LineChart';
import { BAND_COLOR } from '../../shared/viz/tokens';
import type { ConditionBand } from '../../schema/machine';
import { useSourceDrawer } from '../issues/useSourceDrawer';
import { TabLoader } from './TabLoader';
import { availabilitySeries, machineTimeline, type TimelineEvent } from './MachineScene.transform';

export interface MachineHistoryTabProps {
  machineId: string;
  band: ConditionBand;
  /** Component filter from the illustration or the register, or null. */
  component: string | null;
  onClearComponent: () => void;
}

const CARD =
  'rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg';

/** Demo line 2205 — now in `shared/seed/machine-ask.ts` so the assistant gate can harvest it. */
const STARTERS = MACHINE_ASK_STARTERS;

export function MachineHistoryTab({
  machineId,
  band,
  component,
  onClearComponent,
}: MachineHistoryTabProps) {
  const orders = useOemData('work-orders');
  const telemetry = useOemData('telemetry');
  const service = useOemData('service');
  const comms = useOemData('comms');
  const { open } = useDrawer();
  const { openDataset } = useSourceDrawer();

  const timeline = useMemo(() => {
    if (!orders.data) return null;
    return machineTimeline(
      machineId,
      { workOrders: unpackTable<WorkOrder>(orders.data.wos) },
      {
        faults: telemetry.data?.events_critical ?? null,
        cases: service.data?.cases ?? null,
        emails: comms.data?.emails ?? null,
      },
      component,
    );
  }, [orders.data, telemetry.data, service.data, comms.data, machineId, component]);

  const availability = useMemo(
    () =>
      telemetry.data
        ? availabilitySeries(machineId, unpackTable<ShiftDailyRow>(telemetry.data.shift_daily))
        : null,
    [telemetry.data, machineId],
  );

  const pending = [
    telemetry.loading ? 'faults' : null,
    service.loading ? 'cases' : null,
    comms.loading ? 'emails' : null,
  ].filter(Boolean);

  // Demo line 2201: each all-in figure opens its order with the calculation.
  const openWorkOrder = (w: WorkOrder) =>
    openDataset('wos', {
      title: `Work order ${w.wo_id}`,
      filter: { wo_id: w.wo_id },
      calc: workOrderAllInCalc(w),
    });

  const openEmail = (event: TimelineEvent) => {
    if (!event.file) return;
    const payload: SourceDocPayload = {
      kind: 'doc',
      title: event.title ?? event.file,
      path: event.file,
      conn: 'Outlook',
      text:
        comms.data?.email_bodies[event.file] ??
        'The body of this email is not in the extracted pack.',
    };
    open('oem-source', {
      title: payload.title,
      data: payload as unknown as Record<string, unknown>,
    });
  };

  return (
    <TabLoader
      loading={orders.loading || !timeline}
      error={orders.error}
      what="Work orders"
      rows={10}
    >
      {timeline ? (
        <div className="@container">
          <div className="grid grid-cols-1 gap-4 @4xl:grid-cols-[minmax(0,1fr)_320px]">
            <section className={CARD}>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Machine history{component ? ` · ${component}` : ''}
                {component ? (
                  <button
                    type="button"
                    onClick={onClearComponent}
                    className="ml-2 text-xs font-normal text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                  >
                    Show all
                  </button>
                ) : null}
              </h3>
              <p className="mb-3 mt-0.5 text-[11px] text-muted">
                {timeline.events.length} events · {timeline.summary.orders} work orders ·{' '}
                {timeline.summary.breakdowns} breakdowns ·{' '}
                <button
                  type="button"
                  onClick={() =>
                    openDataset('wos', {
                      title: `Work orders · ${machineId}`,
                      filter: { machine_id: machineId },
                    })
                  }
                  title="Open the work orders behind this figure"
                  className="mono font-semibold text-zinc-900 dark:text-zinc-100 rounded underline decoration-dotted decoration-1 underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                >
                  {timeline.summary.totalLabel}
                </button>{' '}
                all-in over 18 months (SAP PM + ledger). Newest first.
                {pending.length ? ` Loading ${pending.join(', ')}…` : null}
              </p>

              <ol className="divide-y divide-[var(--hairline)]">
                {timeline.events.map((event) => (
                  <li key={event.key} className="grid grid-cols-[76px_minmax(0,1fr)] gap-3 py-2">
                    <time
                      className="mono text-[11px] leading-tight text-muted"
                      dateTime={event.date}
                    >
                      {event.date}
                      <span className="block text-[10px]">{event.tag}</span>
                    </time>
                    <div className="min-w-0 text-[12.5px] text-zinc-700 dark:text-zinc-300">
                      {event.workOrder ? (
                        <WorkOrderEvent row={event.workOrder} onOpen={openWorkOrder} />
                      ) : (
                        <>
                          <b className="text-zinc-900 dark:text-zinc-100">{event.title}</b>{' '}
                          <span className="text-muted">{event.meta}</span>
                          {event.detail ? <> {event.detail}</> : null}
                          {event.kind === 'email' ? (
                            <>
                              {' '}
                              <button
                                type="button"
                                onClick={() => openEmail(event)}
                                className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                              >
                                open
                              </button>
                            </>
                          ) : null}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              <p className="mt-2 text-[11px] text-muted">
                All-in = labour + parts + freight + external + lost production (downtime hours × the
                class rate at this site).
              </p>
            </section>

            <div className="space-y-4">
              <AskBox machineId={machineId} />
              <section className={CARD}>
                <p className="text-xs uppercase tracking-wider text-muted">
                  Availability · last 12 days
                </p>
                {availability ? (
                  <LineChart
                    labels={availability.map((p) => p.label)}
                    series={[
                      {
                        values: availability.map((p) => p.value),
                        color: BAND_COLOR[band],
                        area: true,
                        dots: true,
                      },
                    ]}
                    min={0}
                    max={100}
                    ticks={5}
                    width={300}
                    height={170}
                    xTitle="Date (2026)"
                    yTitle="Availability %"
                  />
                ) : (
                  <p className="mt-2 text-[11px] text-muted">
                    {telemetry.error ? 'Shift records did not load.' : 'Loading…'}
                  </p>
                )}
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </TabLoader>
  );
}

function WorkOrderEvent({
  row,
  onOpen,
}: {
  row: NonNullable<TimelineEvent['workOrder']>;
  /** Opens the order's row with the all-in calculation — demo line 2201. */
  onOpen: (w: WorkOrder) => void;
}) {
  const w = row.order;
  return (
    <>
      <b className="mono text-zinc-900 dark:text-zinc-100">{w.wo_id}</b>{' '}
      <span className="text-muted">
        {w.wo_type}
        {w.pm_code ? ` · ${w.pm_code}` : ''}
      </span>{' '}
      {w.component !== 'Machine - general' ? `${w.component} ` : ''}
      {w.description || ''}{' '}
      <span className="mono whitespace-nowrap text-muted">
        {row.isDraft ? `${w.status} · ` : ''}
        {row.downtimeHours ? `${row.downtimeHours} h down · ` : ''}
        <button
          type="button"
          onClick={() => onOpen(w)}
          title="Open the work order behind this figure"
          className="rounded underline decoration-dotted decoration-1 underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
        >
          {row.allInLabel}
        </button>
      </span>
      {w.root_cause ? (
        <span className="mt-0.5 block text-[11px] text-muted">Root cause: {w.root_cause}</span>
      ) : null}
    </>
  );
}

/** Demo line 2205. "Ask about this machine" — sends to the assistant rail. */
function AskBox({ machineId }: { machineId: string }) {
  const [question, setQuestion] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = question.trim();
    if (!q) return;
    askOem(machineAskPrompt(q, machineId));
    setQuestion('');
  };

  return (
    <section className={CARD}>
      <p className="text-xs uppercase tracking-wider text-muted">Ask about this machine</p>
      <form onSubmit={submit} className="mt-2 flex gap-1.5">
        <label className="sr-only" htmlFor={`ask-${machineId}`}>
          Ask about {machineId}
        </label>
        <input
          id={`ask-${machineId}`}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={`e.g. why does ${machineId} keep breaking down?`}
          className="min-w-0 flex-1 rounded-md border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] px-2 py-1.5 text-[12.5px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:text-zinc-100"
        />
        <button
          type="submit"
          className="rounded-md bg-accent-600 px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-accent-700"
        >
          Ask
        </button>
      </form>
      <div className="mt-2 flex flex-col gap-1">
        {STARTERS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => askOem(machineAskPrompt(q, machineId))}
            className="inline-flex items-start gap-1.5 rounded-md px-2 py-1 text-left text-[12px] text-zinc-600 transition-colors hover:bg-[var(--glass-surface-strong)] hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100"
          >
            <Sparkles className="mt-0.5 h-3 w-3 flex-shrink-0 text-zinc-400" aria-hidden />
            {q}
          </button>
        ))}
      </div>
    </section>
  );
}
